import {
  ChargeSessionCodeProvenance,
  ChargeSessionCodeType,
  ChargeSessionProviderRole,
  ChargeSessionType,
  InvoiceTypes
} from '../../enums';

import * as evidenceConverters from '../artifacts/evidence-converter';
import { convertDX } from '../artifacts/evidence-converter';
import { cloneDeep, groupBy } from '../object-utils';

import * as commonEnums from '../../enums';
import { emptyInvoice } from './empty-invoice';
import { emptyCodableTransaction } from './empty-codable-transaction';
import { CodableStatus } from '../amplify/process-states';
import { extractCodeDescriptionForCode } from '../amplify/utils/dictionary/code-description-dictionary-utils';

// --------------- types --------------- //

type IssueMap = {
  [key: string]: CMxCommonApp.CodeEditInputs;
};
type IssueFilter = (
  invoice: InvoicesAPI.Invoice,
  issueMap: IssueMap
) => CMxCommonApp.CodeEditInputs;

type UnifiedInvoice = InvoicesAPI.Invoice | AmplifyAPI.Invoice;
type UnifiedTransaction =
  | InvoicesAPI.FinancialTransaction
  | AmplifyAPI.Transaction;

// --------------- helper functions --------------- //

const _getSharedCodes = () => {
  // returns a new object for populating shared codes.
  return {
    diagnoses: [],
    modifiers: []
  };
};

const _getValueOrNull = (prop: any) => {
  if (prop === 'null') {
    return null;
  } else if (prop === undefined) {
    return null;
  } else {
    return prop;
  }
};
const _getTechCode = technical_code => {
  return 'code' in technical_code
    ? technical_code.code
    : technical_code.technical_code;
};

const _convertTechCode = (
  txn: UnifiedTransaction,
  editable: boolean,
  codeIssues: CMxCommonApp.EditorIssue[] = []
): CMxCommonApp.ChargeSessionCode => {
  const { technical_code } = txn;
  const codeName = _getTechCode(technical_code);

  return {
    codeType: ChargeSessionCodeType.PROCEDURE,
    codeIssues,
    provenance: ChargeSessionCodeProvenance.TECH,
    code: codeName,
    units: technical_code.units,
    hash: `${txn.guid}:${codeName}`.replace(/(\.|\:)/gi, '__'),
    unitable: true,
    editable: editable,
    evidence: [], // TORESOLVE: do we have evidence for technical codes
    description: '',
    codeProvenance: (technical_code as any).code_provenances ?? []
  };
};

const _convertModifier = (
  txn: UnifiedTransaction,
  modifier: InvoicesAPI.Modifier,
  codeIssues: CMxCommonApp.EditorIssue[] = []
): CMxCommonApp.ChargeSessionCode => {
  return {
    codeType: ChargeSessionCodeType.MODIFIER,
    provenance: ChargeSessionCodeProvenance.MODIFIER,
    code: modifier.modifier_code,
    hash: `${txn.guid}:${modifier.modifier_code}`,
    units: 0,
    unitable: false,
    editable: true,
    codeIssues,
    evidence: [], // there isn't really evidence for modifiers as these are applied by rules.
    description: '',
    codeProvenance: modifier.code_provenances
  };
};

const _convertDX = (
  txn: UnifiedTransaction,
  dx: InvoicesAPI.DiagnosticCode,
  codeIssues: CMxCommonApp.EditorIssue[] = []
): CMxCommonApp.ChargeSessionCode => {
  const id = `${txn.guid}:${dx.diagnosis_code}`.replace(/(\.|\:)/gi, '__');

  return {
    //@ts-ignore
    active: !!dx.active,
    codeType: ChargeSessionCodeType.DIAGNOSIS,
    provenance: ChargeSessionCodeProvenance.ICD,
    code: dx.diagnosis_code,
    units: 0,
    unitable: false,
    editable: true,
    codeIssues,
    hash: id,
    evidence: convertDX(dx, id, true),
    //@ts-ignore
    shared: dx.shared ?? false,
    description: '',
    codeProvenance: dx.code_provenances
  };
};

const _convertProcedure = (
  txn: UnifiedTransaction,
  editable: boolean = true,
  codeIssues: CMxCommonApp.EditorIssue[] = []
): CMxCommonApp.ChargeSessionCode => {
  const { procedure } = txn;
  const hash = `${txn.guid}:${procedure.procedure_code}`.replace(
    /(\.|\:)/gi,
    '__'
  );
  procedure.procedure_code;
  return {
    codeType: ChargeSessionCodeType.PROCEDURE,
    provenance: ChargeSessionCodeProvenance.CPT,
    code: procedure.procedure_code,
    units: procedure.units,
    unitable: true,
    editable: editable,
    hash,
    codeIssues,
    evidence: evidenceConverters.convertCPT(procedure, hash, true),
    description: 'procedure_name' in procedure ? procedure.procedure_name : '',
    codeProvenance: procedure.code_provenances ?? []
  };
};

const getIssues = (code: string, codeEdits: CMxCommonApp.CodeEditInputs) => {
  if (codeEdits[code]) {
    return codeEdits[code];
  }
  return [];
};

const _convertCodes = (
  txn: UnifiedTransaction,
  shared_codes: CMxCommonApp.SharedCodes,
  codeEdits: CMxCommonApp.CodeEditInputs = {}
) => {
  const codes = [];
  const hasDX = txn.diagnoses?.length ?? false;

  // procedures and technical codes are 1:1 with a transaction
  if (txn.procedure) {
    const editable = (txn.procedure?.units ?? 0) >= 0;
    const code = txn.procedure?.procedure_code ?? '';
    const edits = getIssues(`${code}`, codeEdits);
    codes.push(_convertProcedure(txn, editable, edits));
  }

  if (txn.technical_code) {
    const { technical_code } = txn;
    const code =
      'code' in technical_code
        ? technical_code.code
        : technical_code.technical_code;
    const edits = getIssues(`${code}`, codeEdits);
    codes.push(_convertTechCode(txn, true, edits));
  }

  // txn.modifiers is deprecated.
  const getValidModifiers = modifiers =>
    Array.isArray(modifiers) && modifiers.length > 0 ? modifiers : null;

  const procedureModifiers =
    getValidModifiers(txn.procedure?.modifier_codes) ?? [];
  const technicalModifiers =
    getValidModifiers(txn.technical_code?.modifier_codes) ?? [];
  const allModifiers =
    procedureModifiers.length > 0 || technicalModifiers.length > 0
      ? [...procedureModifiers, ...technicalModifiers]
      : txn.modifiers ?? [];

  const uniqueModifiers = Array.from(
    new Set(allModifiers.map(mod => mod.modifier_code))
  ).map(modifier_code =>
    allModifiers.find(mod => mod.modifier_code === modifier_code)
  );

  const modifierCodes = uniqueModifiers;

  if (modifierCodes) {
    _convertModifiers(modifierCodes, shared_codes, txn, codes, codeEdits);
  }

  if (hasDX) {
    const exemptDX = new Set<string>(
      [...shared_codes.diagnoses].map(code => code.diagnosis_code)
    );
    txn.diagnoses.forEach(dx => {
      const code = dx.diagnosis_code ?? '';
      if (!exemptDX.has(code)) {
        const edits = getIssues(`${code}`, codeEdits);
        codes.push(_convertDX(txn, dx, edits));
      }
    });
  }

  return codes;
};

const _convertModifiers = (
  modifierCodes: CMxAPI.Modifier[] | undefined,
  shared_codes: CMxCommonApp.SharedCodes,
  txn: UnifiedTransaction,
  codes: any[],
  codeEdits: CMxCommonApp.CodeEditInputs
) => {
  if (modifierCodes?.length) {
    const exemptMods = new Set<string>(
      [...shared_codes.modifiers].map(code => code.modifier_code)
    );
    modifierCodes.forEach(mod => {
      const modCode = mod.modifier_code ?? '';
      if (!exemptMods.has(modCode)) {
        const modEdits = getIssues(`${modCode}`, codeEdits);
        codes.push(_convertModifier(txn, mod, modEdits));
      }
    });
  }
};

const _determineChargeSessionType = (txn: UnifiedTransaction) => {
  if (txn.procedure && txn.technical_code) {
    return ChargeSessionType.PRO_TECH;
  } else if (txn.procedure) {
    return ChargeSessionType.PROFESSIONAL;
  } else if (txn.technical_code) {
    return ChargeSessionType.TECHNICAL;
  } else {
    // this will need to be changed once we have to support
    // empty charge sessions.  at that point we will be unable
    // to determine the charge session type just from the presence or
    // absence of a charge.  for now, just to default to professional
    // our most common charge type
    return ChargeSessionType.PROFESSIONAL;
  }
};

const _assembleProviders = (invoice: UnifiedInvoice) => {
  // for the moment, this is just the billing provider,
  // we might need to add more here.
  const billingProvider: CMxCommonApp.ChargeSessionProvider = {
    provider: {
      name: invoice.billing_provider_name,
      number: invoice.billing_provider_number,
      specialty: `not available` // this was never used afaict
    },
    providerRole: ChargeSessionProviderRole.BILLING
  };
  return [billingProvider];
};

const _assembleBillingInformation = (invoice: UnifiedInvoice) => {
  return {
    billingArea: invoice.billing_area,
    billingLocation: invoice.billing_location
  };
};

const _buildChargeFromInvoiceAndTransaction = (
  invoice: UnifiedInvoice,
  txn: InvoicesAPI.FinancialTransaction | AmplifyAPI.Transaction,
  codeEdits: CMxCommonApp.CodeEditInputs,
  shared_codes: CMxCommonApp.SharedCodes
): CMxCommonApp.ChargeSession => {
  const chargeSession: CMxCommonApp.ChargeSession = {
    // field definitions will be filled out in the
    // applications, not ui-common.  it's just easier
    // not to have partials.
    fieldDefinitions: [],
    chargeType: _determineChargeSessionType(txn),
    providers: _assembleProviders(invoice),
    billingInformation: _assembleBillingInformation(invoice),
    codes: _convertCodes(txn, shared_codes, codeEdits),
    transactionType: 'type' in txn ? txn.type : 'ASSEMBLED',
    transactionId: txn.guid,
    invoiceId: invoice.guid,
    processInstanceId: '' // process instance will be attached later.
  };

  return chargeSession;
};
const _assemebledFilter: IssueFilter = (invoice, issueMap) => {
  if (invoice.type === 'ASSEMBLED' && issueMap.ASSEMBLED) {
    return issueMap.ASSEMBLED;
  }
  return {};
};

// Helper function to process modifiers
const _processModifiers = (modifiers, invoices, getModifiers, sharedCodes) => {
  modifiers.forEach(modifier => {
    const inEvery = invoices.every(invoice => {
      return (invoice.transactions ?? []).every(txn => {
        return (getModifiers(txn) ?? []).find(
          targetMod => targetMod.modifier_code === modifier.modifier_code
        );
      });
    });

    if (inEvery) {
      sharedCodes.modifiers.push(modifier);
    }
  });
};

const _determineSharedCodes = (invoices: UnifiedInvoice[]) => {
  const invoiceTypes = groupBy(invoices, 'type');
  const sharedCodeMap = {};

  Object.keys(invoiceTypes).forEach(key => {
    const invoices = invoiceTypes[key];
    const sharedCodes = {
      diagnoses: [],
      modifiers: []
    };
    sharedCodeMap[key] = sharedCodes;

    const totalLen = invoices
      .map(invoice => {
        return invoice.transactions?.length ?? 0;
      })
      .reduce((partialSum, a) => partialSum + a, 0);

    if (totalLen > 1) {
      const [firstInvoice] = invoices;
      const [firstTxn] = firstInvoice.transactions;

      if (firstTxn?.diagnoses?.length) {
        firstTxn.diagnoses.forEach(dx => {
          const inEvery = invoices.every(invoice => {
            //@ts-ignore
            return (invoice.transactions ?? []).every(txn => {
              return txn.diagnoses.find(
                diag => diag.diagnosis_code === dx.diagnosis_code
              );
            });
          });

          if (inEvery) {
            sharedCodes.diagnoses.push(dx);
          }
        });
      }

      if (firstTxn.procedure?.modifier_codes?.length) {
        _processModifiers(
          firstTxn.procedure.modifier_codes,
          invoices,
          txn => txn.procedure?.modifier_codes,
          sharedCodes
        );
      }

      if (firstTxn.technical_code?.modifier_codes?.length) {
        _processModifiers(
          firstTxn.technical_code.modifier_codes,
          invoices,
          txn => txn.technical_code?.modifier_codes,
          sharedCodes
        );
      }

      // Support txn.modifiers for backwards compatibility
      if (firstTxn.modifiers?.length) {
        _processModifiers(
          firstTxn.modifiers,
          invoices,
          txn => txn.modifiers,
          sharedCodes
        );
      }
    }
  });

  return sharedCodeMap;
};

// --------------- api --------------- //

const convertInvoicesToCharges = (
  processInstance: AmplifyAPI.ProcessInstance,
  codeEdits: CMxCommonApp.CodeEditInputs = {},
  findSharedCodes: boolean = false
): CMxCommonApp.ChargeSession[] => {
  if (!Array.isArray(processInstance.invoices)) {
    throw new Error(`no invoices array `);
  }
  const chargeSessions = [];

  processInstance?.invoices?.forEach(invoice => {
    const sharedCodes = _getSharedCodes();

    invoice?.transactions?.forEach(txn => {
      const chargeSession = _buildChargeFromInvoiceAndTransaction(
        invoice,
        txn,
        codeEdits,
        sharedCodes
      );
      chargeSession.processInstanceId = processInstance.guid.uuid;
      chargeSessions.push(chargeSession);
    });
  });

  return chargeSessions;
};

const convertInvoicesToCodable = (
  processInstance: AmplifyAPI.ProcessInstance,
  codeEdits: CMxCommonApp.CodeEditInputs = {},
  findSharedCodes: boolean = false
): CMxCommonApp.CodableInvoice[] => {
  if (!Array.isArray(processInstance.invoices)) {
    throw new Error(`no invoices array `);
  }
  const invoices: AmplifyAPI.Invoice[] = cloneDeep(processInstance.invoices);
  const codableInvoices: CMxCommonApp.CodableInvoice[] = invoices.map(
    invoice => {
      const transactions: CMxCommonApp.CodableTransaction[] =
        invoice?.transactions?.length > 0
          ? invoice?.transactions?.map(txn => {
              const { codes } = _buildChargeFromInvoiceAndTransaction(
                invoice,
                txn,
                codeEdits,
                _getSharedCodes()
              );
              return {
                ...txn,
                codes,
                // this is necessary to get around a typescript issue.
                errorMessages: txn.errorMessages ?? [],
                fieldDefinitions: []
              };
            })
          : [];
      return {
        ...invoice,
        // this is necessary to get around a typescript issue.
        errorMessages: invoice.errorMessages ?? [],
        transactions
      };
    }
  );

  const codable =
    CodableStatus.has(processInstance.status) ||
    (processInstance.status === commonEnums.AccelerateProcessStatus.SUBMITTED &&
      processInstance.qaStatus === commonEnums.ProcessQAStatus.IN_QA);

  const { sharedDiagnosisCodes = [] } = processInstance;
  if (sharedDiagnosisCodes.length || codable) {
    const diagnosis: CMxCommonApp.ChargeSessionCode[] = sharedDiagnosisCodes.map(
      (dx, idx) => {
        const code = dx.diagnosis_code ?? '';
        const codeIssues = getIssues(`${code}`, codeEdits);
        const id = `SHARED_CODES:${dx.diagnosis_code}`.replace(
          /(\.|\:)/gi,
          '__'
        );

        return {
          codeType: ChargeSessionCodeType.DIAGNOSIS,
          provenance: ChargeSessionCodeProvenance.ICD,
          code: dx.diagnosis_code,
          diagnosis_code: dx.diagnosis_code,
          sequence_number: dx.sequence_number,
          active: true,
          additionalProperties: {},
          shared: true,
          units: 0,
          unitable: false,
          editable: true,
          codeIssues,
          hash: id,
          evidence: convertDX(dx, id, true),
          description: '',
          codeProvenance: dx.code_provenances
        };
      }
    );

    codableInvoices.unshift({
      ...emptyInvoice,
      type: InvoiceTypes.SHARED_CODES,
      transactions: [
        {
          ...emptyCodableTransaction,
          codes: diagnosis
        }
      ]
    });
  }
  return codableInvoices;
};

const addDescriptionsToCodableInvoices = (
  codableInvoices: CMxCommonApp.CodableInvoice[],
  codeDescriptions
) => {
  return [...codableInvoices].map(codableInvoice => {
    codableInvoice.transactions.forEach(
      (txn: CMxCommonApp.CodableTransaction) => {
        txn.codes.forEach(code => {
          const description: any =
            codeDescriptions[(code as CMxCommonApp.ChargeSessionCode).code];
          (code as CMxCommonApp.ChargeSessionCode).description = extractCodeDescriptionForCode(
            description
          );
        });
      }
    );

    return codableInvoice;
  });
};

const addCategoricalDataToCodableInvoices = (
  codableInvoices: CMxCommonApp.CodableInvoice[],
  caseRun: CMxAPI.CaseRun
) => {
  return [...codableInvoices].map(codableInvoice => {
    codableInvoice.transactions = codableInvoice.transactions.map(
      (txn: CMxCommonApp.CodableTransaction) => {
        txn.codes = txn.codes.map(code => {
          const c = { ...code } as CMxCommonApp.ChargeSessionCode;
          c.evidence = c.evidence.map(orig => {
            const e = { ...orig };

            if (e.featureType === commonEnums.EvidenceType.COMPLETE) {
              e.meta = e.meta ? { ...e.meta } : {};
              if (!e.meta.content) {
                e.meta.content = caseRun[e.fieldName] ?? 'UNKNOWN';
              }
            }
            return e;
          });
          return c;
        });
        return txn;
      }
    );

    return codableInvoice;
  });
};

const simplifiedInvoices = (
  caseRun: CMxAPI.CaseRunV2 | CMxAPI.CaseRun,
  findSharedCodes: boolean = false,
  issueMap: IssueMap = {},
  issueFilter: IssueFilter = _assemebledFilter
): CMxCommonApp.SimplifiedInvoice[] => {
  if (!Array.isArray(caseRun.invoices)) {
    return [];
  } else {
    const sharedCodeMap = findSharedCodes
      ? _determineSharedCodes(caseRun.invoices)
      : {};

    const simplifiedInvoices = caseRun.invoices.map(invoice => {
      const shared_codes = sharedCodeMap[invoice.type] || _getSharedCodes();
      const codeEdits = issueFilter(invoice, issueMap);
      const transactions: CMxCommonApp.SimplifiedTransaction[] = (
        invoice.transactions ?? []
      ).map(txn => {
        return {
          guid: txn.guid,
          type: _determineChargeSessionType(txn),
          department_code: txn.department_code,
          date_of_service: txn.date_of_service,
          codes: _convertCodes(txn, shared_codes, codeEdits),
          description: txn.alt_transaction_description ?? null
        };
      });

      if (shared_codes.diagnoses.length || shared_codes.modifiers.length) {
        const stubbedTxn = { ...shared_codes } as any;

        transactions.push({
          guid: 'not applicable',
          type: 'SHARED_CODES',
          department_code: 'department_code',
          codes: _convertCodes(stubbedTxn, _getSharedCodes(), codeEdits),
          description: 'shared codes'
        });
      }

      const {
        guid,
        billing_provider_number,
        ordering_provider_number,
        performing_provider_number,
        referring_provider_number
      } = invoice;

      return {
        processInstanceGuid: caseRun.process_instance_guid,
        type: invoice.type,
        // Plucked as-is from original invoice
        guid,
        billing_provider_number,
        ordering_provider_number,
        performing_provider_number,
        referring_provider_number,
        transactions,
        // The following are not always present in Automate
        // or if they are present, they are sometimes present
        // as the string 'null'.
        billing_area: _getValueOrNull(invoice.billing_area),
        billing_provider_name: _getValueOrNull(invoice.billing_provider_name),
        ordering_provider_name: _getValueOrNull(invoice.ordering_provider_name),
        performing_provider_name: _getValueOrNull(
          invoice.performing_provider_name
        ),
        referring_provider_name: _getValueOrNull(
          invoice.referring_provider_name
        ),
        // Automate only for the time being.
        shared_codes: shared_codes,
        // Amplify only
        billing_location: null,
        patient_type: null,
        billing_provider_type: _getValueOrNull(invoice?.billing_provider_type),
        ordering_provider_type: _getValueOrNull(
          invoice?.ordering_provider_type
        ),
        referring_provider_type: _getValueOrNull(
          invoice?.referring_provider_type
        ),
        performing_provider_type: _getValueOrNull(
          invoice?.performing_provider_type
        )
      };
    });

    return simplifiedInvoices;
  }
};

export {
  addDescriptionsToCodableInvoices,
  addCategoricalDataToCodableInvoices,
  convertInvoicesToCharges,
  simplifiedInvoices,
  convertInvoicesToCodable
};
